import type { ScenarioCurveType } from './scenarios';
import { apiClient } from './client';

export interface LayerEconomicsParams {
  layer_id: string;
  capex_production_well: number;
  capex_injection_well: number;
  opex_production_well: number;
  opex_injection_well: number;
  oil_price: number;
  injection_unit_cost: number;
  abandonment_per_well: number;
  additional_cost_per_active_well?: number | null;
  additional_cost_per_year?: number | null;
  additional_cost_per_oil_unit?: number | null;
  layer_economy_template_id?: string | null;
}

export interface FieldEconomicsParams {
  discount_rate: number;
  currency: string;
  royalty_rate?: number | null;
  transport_per_unit?: number | null;
  income_tax_rate?: number | null;
  fixed_field_opex_per_year?: number | null;
  field_economy_template_id?: string | null;
}

export interface ScenarioEconomicsParamsResponse {
  field: FieldEconomicsParams;
  layers: LayerEconomicsParams[];
}

export interface ScenarioEconomicsCalculateRequest {
  field: FieldEconomicsParams;
  layers: LayerEconomicsParams[];
}

export interface ScenarioEconomicsParamsUpdate {
  field: FieldEconomicsParams;
  layers: LayerEconomicsParams[];
}

export interface EconomicsYearlyPoint {
  year: number;
  oil: number;
  liquid: number;
  gross_revenue: number;
  royalty_cost: number;
  transport_cost: number;
  revenue: number;
  capex: number;
  opex: number;
  injection_volume: number;
  injection_cost: number;
  abandonment: number;
  additional_cost: number;
  fixed_field_opex: number;
  tax: number;
  cash_flow: number;
  discounted_cash_flow: number;
  cumulative_cash_flow: number;
  active_production_wells: number;
  active_injection_wells: number;
}

export interface LayerEconomicsResult {
  layer_id: string;
  layer_name: string;
  npv: number;
  yearly: EconomicsYearlyPoint[];
}

export interface EconomicsVariantResult {
  variant: ScenarioCurveType;
  npv: number;
  yearly: EconomicsYearlyPoint[];
  layers: LayerEconomicsResult[];
}

export interface ScenarioEconomicsCalculateResponse {
  scenario_id: string;
  currency: string;
  discount_rate: number;
  unit_id: number;
  variants: EconomicsVariantResult[];
}

export interface ScenarioEconomicsStatusResponse {
  has_results: boolean;
  is_up_to_date: boolean;
  production_is_up_to_date: boolean;
  unit_matches: boolean;
  current_unit_id: number;
  economics_unit_id?: number | null;
  economics_calculated_at?: string | null;
  production_calculated_at?: string | null;
}

export const scenarioEconomicsApi = {
  getParams: async (scenarioId: string): Promise<ScenarioEconomicsParamsResponse> => {
    return apiClient.get<ScenarioEconomicsParamsResponse>(
      `/scenarios/${scenarioId}/economics/params`,
    );
  },

  saveParams: async (
    scenarioId: string,
    payload: ScenarioEconomicsParamsUpdate,
  ): Promise<ScenarioEconomicsParamsResponse> => {
    return apiClient.put<ScenarioEconomicsParamsResponse>(
      `/scenarios/${scenarioId}/economics/params`,
      payload,
    );
  },

  getResults: async (scenarioId: string): Promise<ScenarioEconomicsCalculateResponse> => {
    return apiClient.get<ScenarioEconomicsCalculateResponse>(
      `/scenarios/${scenarioId}/economics/results`,
    );
  },

  getStatus: async (scenarioId: string): Promise<ScenarioEconomicsStatusResponse> => {
    return apiClient.get<ScenarioEconomicsStatusResponse>(
      `/scenarios/${scenarioId}/economics/status`,
    );
  },

  calculate: async (
    scenarioId: string,
    payload: ScenarioEconomicsCalculateRequest,
  ): Promise<ScenarioEconomicsCalculateResponse> => {
    return apiClient.post<ScenarioEconomicsCalculateResponse>(
      `/scenarios/${scenarioId}/economics/calculate`,
      payload,
    );
  },
};
